/**
 * Monthlees Magnus PR-2 — agent embed bridge (contract v1.0).
 * Session token from App Proxy only. postMessage origin-checked both ways.
 * Cart payloads: ids/qty + line properties only — never health data.
 */
(function () {
  'use strict';

  var DRAFT_KEY = 'monthlees_guide_draft';
  var RESUME_KEY = 'monthlees_guide_resume';
  var ENTERED_KEY = 'monthlees_guide_entered';
  var PENDING_OPEN_KEY = 'monthlees_guide_pending_open';

  var MSG = {
    READY: 'MONTHLEES_AGENT_READY',
    INIT: 'MONTHLEES_AGENT_INIT',
    AUTH_EXPIRED: 'MONTHLEES_AUTH_EXPIRED',
    AUTH_TOKEN: 'MONTHLEES_AUTH_TOKEN',
    APPROVE: 'MONTHLEES_APPROVE',
    ADD_TO_CART: 'MONTHLEES_ADD_TO_CART',
    CART_RESULT: 'MONTHLEES_CART_RESULT',
    RESIZE: 'MONTHLEES_RESIZE',
    NAVIGATE: 'MONTHLEES_NAVIGATE',
    THREAD_STATE: 'MONTHLEES_THREAD_STATE',
  };

  var root = null;
  var config = null;
  var frame = null;
  var panel = null;
  var gate = null;
  var statusEl = null;
  var echoEl = null;
  var appOrigin = '';
  var session = null;
  var ready = false;
  var initSent = false;
  var openOpts = null;
  var bound = false;
  var guideState = 'idle';
  var capabilitiesPayload = null;
  var capabilitiesPromise = null;
  var seededQueue = null;
  var listeners = {};

  var CAPABILITIES_ENDPOINT = '/apps/precision/agent/capabilities';

  /** Copy binding map — never inline capability promises in Liquid/JS callers. */
  var COPY = {
    pdp_2a: {
      labs: 'he checks your labs and current formula first.',
      formula: "he checks your current formula and what you're already taking.",
    },
    dashboard_1k: {
      ready: 'Magnus has your {{panel}} panel and current formula',
      upload: 'Magnus can read your labs once you upload them',
    },
  };

  function captureEvent(name, props) {
    if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') {
      window.MonthleesAnalytics.track(name, props || {});
      return;
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(name, props || {});
    }
  }

  function emit(eventName, detail) {
    var list = listeners[eventName] || [];
    for (var i = 0; i < list.length; i += 1) {
      try {
        list[i](detail);
      } catch (e) {
        /* ignore listener errors */
      }
    }
  }

  function setGuideState(next) {
    guideState = next || 'idle';
    try {
      document.documentElement.setAttribute('data-magnus-state', guideState);
    } catch (e) {
      /* ignore */
    }
    emit('state', guideState);
  }

  function emptyCapabilities() {
    return {
      ok: false,
      member: { signed_in: false, is_member: false },
      labs: { has_labs: false, panel_label: '', latest_draw: null, marker_count: 0, stale: false },
      formula: { has_formula: false, capsule_count: 0, status: '', primary_pattern: '', updated_at: null },
      regimen: { count: 0 },
      capabilities: {
        labs_check: false,
        formula_check: false,
        regimen_check: false,
        overlap_check: false,
        citations: false,
        orders: false,
        build_formula: false,
      },
    };
  }

  function fetchCapabilitiesOnce() {
    if (capabilitiesPromise) return capabilitiesPromise;
    capabilitiesPromise = fetch(CAPABILITIES_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-store',
      },
    })
      .then(function (res) {
        return res
          .json()
          .then(function (data) {
            if (!data || data.ok !== true) return emptyCapabilities();
            return data;
          })
          .catch(function () {
            return emptyCapabilities();
          });
      })
      .catch(function () {
        return emptyCapabilities();
      })
      .then(function (payload) {
        capabilitiesPayload = payload;
        emit('capabilities', payload);
        applyCapabilityDom(payload);
        return payload;
      });
    return capabilitiesPromise;
  }

  function applyCapabilityDom(payload) {
    var caps = (payload && payload.capabilities) || {};
    var labs = (payload && payload.labs) || {};
    var formula = (payload && payload.formula) || {};
    var ok = !!(payload && payload.ok);

    // Citations proof row — gated on capabilities.citations (evidence DB).
    document.querySelectorAll('[data-magnus-proof-citations]').forEach(function (el) {
      el.hidden = !caps.citations;
    });

    // PDP 2a — hide entirely on failure; otherwise bind claim string.
    document.querySelectorAll('[data-magnus-pdp-nudge]').forEach(function (nudge) {
      if (!ok) {
        nudge.hidden = true;
        nudge.setAttribute('aria-hidden', 'true');
        return;
      }
      nudge.hidden = false;
      nudge.setAttribute('aria-hidden', 'false');
      var claim = nudge.querySelector('[data-magnus-pdp-claim]');
      if (claim) {
        claim.textContent = caps.labs_check ? COPY.pdp_2a.labs : COPY.pdp_2a.formula;
      }
      var overlap = nudge.querySelector('[data-magnus-pdp-overlap]');
      if (overlap) {
        overlap.hidden = !caps.overlap_check;
      }
    });

    // Dashboard 1k
    document.querySelectorAll('[data-magnus-dashboard-entry]').forEach(function (entry) {
      var copyEl = entry.querySelector('[data-magnus-dashboard-copy]');
      var uploadEl = entry.querySelector('[data-magnus-dashboard-upload]');
      if (!copyEl) return;
      if (labs.has_labs && formula.has_formula) {
        var panel = labs.panel_label || 'recent';
        copyEl.textContent = COPY.dashboard_1k.ready.replace('{{panel}}', panel);
        if (uploadEl) uploadEl.hidden = true;
      } else {
        copyEl.textContent = COPY.dashboard_1k.upload;
        if (uploadEl) uploadEl.hidden = false;
      }
    });

    // Cited-science section — must not render while capabilities.citations is false
    document.querySelectorAll('[data-magnus-citations-gate]').forEach(function (el) {
      if (caps.citations) {
        el.hidden = false;
        el.setAttribute('aria-hidden', 'false');
      } else {
        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function showOptimisticEcho(message) {
    if (!echoEl) echoEl = root && root.querySelector('[data-mg-embed-echo]');
    if (!echoEl) return;
    var text = String(message || '').trim();
    if (!text) {
      echoEl.hidden = true;
      echoEl.textContent = '';
      return;
    }
    echoEl.hidden = false;
    echoEl.textContent = 'You: “' + text + '”';
    setChromeSubtitle('active');
  }

  /**
   * Drawer chrome subtitle modes:
   *   empty     — precision/default empty thread
   *   commerce  — commerce scope, empty thread
   *   active    — hide after first message (conversation owns the space)
   *   formula   — formula exists in-thread
   */
  function setChromeSubtitle(mode) {
    if (!root) return;
    var title = root.querySelector('[data-mg-embed-title]');
    if (!title) return;
    var next = mode || 'empty';
    root.setAttribute('data-mg-subtitle', next);
    root.classList.toggle('mg-embed--subtitle-collapsed', next === 'active');

    if (next === 'active') {
      title.setAttribute('hidden', '');
      title.setAttribute('aria-hidden', 'true');
      return;
    }

    title.removeAttribute('hidden');
    title.setAttribute('aria-hidden', 'false');
    var attr =
      next === 'commerce'
        ? 'data-title-commerce'
        : next === 'formula'
          ? 'data-title-formula'
          : 'data-title-empty';
    var text = title.getAttribute(attr) || title.textContent;
    title.textContent = text;
  }

  function syncChromeSubtitleFromOpen(opts, message) {
    opts = opts || {};
    var hasMessage = !!(message && String(message).trim());
    if (hasMessage) {
      setChromeSubtitle('active');
      return;
    }
    if ((opts.scope || '') === 'commerce') {
      setChromeSubtitle('commerce');
      return;
    }
    setChromeSubtitle('empty');
  }

  function readSelectedProduct(opts) {
    opts = opts || {};
    var product = opts.product || null;
    if (product && product.variant_id) return product;

    // Prefer live PDP selection at click time.
    var selected =
      document.querySelector('[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('[data-product-selected-variant]');
    var variantId = opts.variant_id || null;
    if (!variantId && selected) {
      variantId = selected.value || selected.getAttribute('data-product-selected-variant');
    }
    var id = opts.product_id || null;
    var handle = opts.product_handle || '';
    var title = opts.product_title || '';
    var meta = document.querySelector('[data-magnus-pdp-nudge], [data-magnus-composer][data-magnus-context="pdp"]');
    if (meta) {
      if (!id) id = meta.getAttribute('data-magnus-product-id');
      if (!handle) handle = meta.getAttribute('data-magnus-product-handle') || '';
      if (!title) title = meta.getAttribute('data-magnus-product-title') || '';
      if (!variantId) variantId = meta.getAttribute('data-magnus-variant-id');
    }
    if (!id && !variantId && !handle) return null;
    return {
      id: id ? String(id) : null,
      variant_id: variantId ? String(variantId) : null,
      handle: handle || '',
      title: title || '',
    };
  }

  function inferContext(opts) {
    if (opts && opts.context) return opts.context;
    var path = (window.location.pathname || '').toLowerCase();
    if (path.indexOf('/products/') === 0) return 'pdp';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if ((path.indexOf('/pages/magnus') === 0 || path.indexOf('/pages/guide') === 0)) return 'guide_page';
    if (path.indexOf('/pages/precision') === 0 || path.indexOf('/account') === 0) return 'dashboard';
    return 'home';
  }

  function readDraft() {
    try {
      return sessionStorage.getItem(DRAFT_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function writeDraft(value) {
    try {
      sessionStorage.setItem(DRAFT_KEY, String(value || '').trim());
    } catch (e) {
      /* ignore */
    }
  }

  function readResume() {
    try {
      return sessionStorage.getItem(RESUME_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function writeResume(value) {
    try {
      if (value) sessionStorage.setItem(RESUME_KEY, String(value));
      else sessionStorage.removeItem(RESUME_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function markEntered() {
    try {
      localStorage.setItem(ENTERED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  function parseConfig() {
    if (!root) return {};
    var node = root.querySelector('[data-mg-embed-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  }

  function setStatus(message, isError) {
    if (!statusEl) return;
    if (!message) {
      statusEl.hidden = true;
      statusEl.textContent = '';
      statusEl.classList.remove('is-error');
      return;
    }
    statusEl.hidden = false;
    statusEl.textContent = message;
    statusEl.classList.toggle('is-error', !!isError);
  }

  function isOpen() {
    return !!(root && !root.hidden);
  }

  function buildFrameSrc(scope, theme) {
    var base = config.useMock ? config.mockSrc : config.appUrl;
    if (!base) base = config.mockSrc || 'https://chat.monthlees.com';
    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      url = new URL(String(base), window.location.origin);
    }
    url.searchParams.set('scope', scope || config.defaultScope || 'precision');
    url.searchParams.set('theme', theme || config.theme || 'light');
    return url.toString();
  }

  function originFromSrc(src) {
    try {
      return new URL(src, window.location.origin).origin;
    } catch (e) {
      return '';
    }
  }

  function postToApp(type, payload) {
    if (!frame || !frame.contentWindow || !appOrigin) return;
    frame.contentWindow.postMessage({ type: type, payload: payload || {} }, appOrigin);
  }

  function fetchSession() {
    var endpoint = (config && config.sessionEndpoint) || '/apps/precision/agent/session';
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        scope: (openOpts && openOpts.scope) || (config && config.defaultScope) || 'precision',
      }),
    }).then(function (res) {
      if (res.status === 401) {
        return { status: 401, data: null };
      }
      return res
        .json()
        .then(function (data) {
          return { status: res.status, data: data };
        })
        .catch(function () {
          return { status: res.status, data: null };
        });
    });
  }

  function showGate(draft) {
    if (!gate) return;
    gate.hidden = false;
    root.classList.add('mg-embed--gate');
    var quote = root.querySelector('[data-mg-embed-gate-draft]');
    if (quote) {
      quote.textContent = draft ? '“' + draft + '”' : 'Your draft is saved in this browser.';
    }
    var login = root.querySelector('[data-mg-embed-login]');
    if (login) {
      login.href = buildLoginUrl();
    }
    if (frame) frame.setAttribute('aria-hidden', 'true');
  }

  function hideGate() {
    if (!gate) return;
    gate.hidden = true;
    root.classList.remove('mg-embed--gate');
    if (frame) frame.removeAttribute('aria-hidden');
  }

  /** Current storefront URL for post-login return (not hardcoded /pages/magnus). */
  function buildReturnTo() {
    try {
      var returnTo = window.location.pathname + window.location.search;
      if (returnTo.indexOf('mg_open=') === -1) {
        returnTo += (returnTo.indexOf('?') === -1 ? '?' : '&') + 'mg_open=1';
      }
      return returnTo;
    } catch (e) {
      return '/pages/magnus?mg_open=1';
    }
  }

  function buildLoginUrl(returnTo) {
    var path = returnTo || buildReturnTo();
    return '/customer_authentication/login?return_to=' + encodeURIComponent(path);
  }

  function allowsGuestScope(scope) {
    // Commerce concierge is available to guests.
    if (scope === 'commerce') return true;
    // Ads / Magnus landing: mount the live app for guests (app owns formula auth).
    // Without this, precision opens only show the theme sign-in gate and never load the iframe.
    var entry =
      (openOpts && (openOpts.entry_surface || openOpts.context)) || '';
    if (scope === 'precision' && (entry === 'guide_page' || entry === 'homepage_3b' || entry === 'funnel')) {
      return true;
    }
    return false;
  }

  function sendInit() {
    if (!ready || !session || initSent) return;
    // Cold-start queue wins over stale draft — never drop the seeded first message.
    var draft =
      (seededQueue && seededQueue.message) ||
      (openOpts && openOpts.draft) ||
      readDraft();
    var resume = (openOpts && openOpts.resume) || readResume();
    var scope = (openOpts && openOpts.scope) || session.scope || config.defaultScope || 'precision';
    if (!session.token && !allowsGuestScope(scope)) return;
    var customer = (config && config.customer) || {};
    var context = (openOpts && openOpts.context) || inferContext(openOpts);
    var entrySurface = (openOpts && openOpts.entry_surface) || 'unknown';
    var product = (openOpts && openOpts.product) || null;
    var returnTo = buildReturnTo();
    var loginUrl = buildLoginUrl(returnTo);
    // Keep theme gate + app sign-in card aligned to the page the guest was on.
    if (config) config.loginUrl = loginUrl;
    postToApp(MSG.INIT, {
      token: session.token || null,
      scope: scope,
      draft: draft || '',
      resume: resume || null,
      theme: (openOpts && openOpts.theme) || config.theme || 'light',
      context: context,
      entry_surface: entrySurface,
      product: product,
      return_to: returnTo,
      login_url: loginUrl,
      customer: {
        first_name: customer.firstName || '',
        logged_in: !!customer.loggedIn,
      },
    });
    initSent = true;
    setGuideState('live');
    seededQueue = null;
    captureEvent('monthlees_guide_agent_init', {
      scope: scope,
      has_draft: !!draft,
      guest: !session.token,
      context: context,
      entry_surface: entrySurface,
      return_to: returnTo,
    });
    captureEvent('guide_open', {
      entry_surface: entrySurface,
      context: context,
      seeded_length: draft ? String(draft).length : 0,
    });
  }

  function mountFrame(scope) {
    if (!frame) return;
    var src = buildFrameSrc(scope, (openOpts && openOpts.theme) || config.theme);
    appOrigin = originFromSrc(src);
    ready = false;
    initSent = false;
    // Always (re)set src so READY fires for a fresh INIT handshake.
    frame.setAttribute('src', src);
  }

  function openShell(opts) {
    opts = opts || {};
    var message =
      opts.message != null
        ? String(opts.message)
        : opts.draft != null
          ? String(opts.draft)
          : readDraft();
    var context = inferContext(opts);
    var entrySurface = opts.entry_surface || opts.entrySurface || 'unknown';
    var product = readSelectedProduct(opts);

    // Queue seed before session exists; replay via INIT. AUTH_EXPIRED must not drop it.
    if (message) {
      seededQueue = { message: message, entry_surface: entrySurface, context: context };
      writeDraft(message);
    }

    openOpts = {
      draft: message || '',
      resume: opts.resume != null ? opts.resume : readResume(),
      scope: opts.scope || config.defaultScope || 'precision',
      theme: opts.theme || config.theme || 'light',
      surface: opts.surface || 'full',
      context: context,
      entry_surface: entrySurface,
      product: product,
    };

    if (openOpts.resume) writeResume(openOpts.resume);
    markEntered();
    setGuideState('opening');
    showOptimisticEcho(message);

    var surface = openOpts.surface;
    if (surface === 'drawer' && config.enableDrawer === false) surface = 'full';
    if (surface === 'full' && config.enableFull === false) surface = 'drawer';
    if (surface !== 'drawer' && surface !== 'inline') surface = 'full';
    openOpts.surface = surface;

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    root.classList.remove('mg-embed--drawer', 'mg-embed--full', 'mg-embed--inline');
    root.classList.add('mg-embed--' + surface);
    document.documentElement.classList.add('mg-embed-open');
    document.documentElement.classList.toggle('mg-embed-open--drawer', surface === 'drawer');
    document.documentElement.classList.toggle('mg-embed-open--full', surface === 'full');
    document.documentElement.classList.toggle('mg-embed-open--inline', surface === 'inline');

    // Fixed-height chat shells: clear any prior RESIZE inline height.
    if (frame && surface !== 'inline') {
      frame.style.height = '';
      frame.style.minHeight = '';
    }

    hideGate();
    setStatus('');
    session = null;
    ready = false;
    initSent = false;
    syncChromeSubtitleFromOpen(openOpts, message);
    hideCartConfirm();
    // Cart chrome sync must never block iframe mount / session start.
    try {
      fetchCart()
        .then(function (cart) {
          if (cart && cart.item_count != null) syncEmbedCartChrome(cart.item_count);
        })
        .catch(function () {
          /* ignore */
        });
    } catch (e) {
      /* ignore */
    }

    setStatus('Connecting…');
    return fetchSession()
      .then(function (result) {
        if (result.status === 401) {
          if (allowsGuestScope(openOpts.scope)) {
            session = {
              token: null,
              expires_at: null,
              scope: openOpts.scope,
            };
            setStatus('');
            mountFrame(openOpts.scope);
            captureEvent('monthlees_guide_agent_guest', { scope: openOpts.scope });
            return;
          }
          setStatus('');
          showGate(openOpts.draft);
          captureEvent('monthlees_guide_agent_gate', { reason: '401' });
          return;
        }
        if (result.status !== 200 || !result.data || !result.data.token) {
          // App not live yet — still mount mock / iframe so the shell is testable.
          if (config.useMock || allowsGuestScope(openOpts.scope)) {
            session = {
              token: config.useMock ? 'mock-theme-session' : null,
              expires_at: null,
              scope: openOpts.scope,
            };
            setStatus(config.useMock ? 'Mock session (live App Proxy not required)' : '');
            mountFrame(openOpts.scope);
            return;
          }
          setStatus('Unable to start a Magnus session. Please try again.', true);
          captureEvent('monthlees_guide_agent_session_error', { status: result.status });
          return;
        }
        session = {
          token: result.data.token,
          expires_at: result.data.expires_at || null,
          scope: result.data.scope || openOpts.scope,
        };
        setStatus('');
        mountFrame(session.scope || openOpts.scope);
      })
      .catch(function () {
        if (config.useMock || allowsGuestScope(openOpts.scope)) {
          session = {
            token: config.useMock ? 'mock-theme-session' : null,
            expires_at: null,
            scope: openOpts.scope,
          };
          setStatus(config.useMock ? 'Mock session (session endpoint unreachable)' : '');
          mountFrame(openOpts.scope);
          return;
        }
        setStatus('Unable to reach the Magnus session endpoint.', true);
      });
  }

  function closeShell() {
    if (!root) return;
    var lastSurface = (openOpts && openOpts.entry_surface) || 'unknown';
    var lastState = guideState;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.classList.remove(
      'mg-embed--drawer',
      'mg-embed--full',
      'mg-embed--inline',
      'mg-embed--gate'
    );
    document.documentElement.classList.remove(
      'mg-embed-open',
      'mg-embed-open--drawer',
      'mg-embed-open--full',
      'mg-embed-open--inline'
    );
    if (frame) {
      frame.style.height = '';
      frame.style.minHeight = '';
    }
    hideGate();
    setStatus('');
    showOptimisticEcho('');
    hideCartConfirm();
    setChromeSubtitle('empty');
    openOpts = null;
    setGuideState('idle');
    captureEvent('guide_abandon', { entry_surface: lastSurface, last_state: lastState });
  }

  function normalizeVariantId(value) {
    if (value == null || value === '') return null;
    var s = String(value);
    if (s.indexOf('/') !== -1) s = s.split('/').pop();
    var n = parseInt(s, 10);
    return isNaN(n) || n <= 0 ? null : n;
  }

  function normalizePlanId(value) {
    if (value == null || value === '') return null;
    var s = String(value);
    if (s.indexOf('/') !== -1) s = s.split('/').pop();
    var n = parseInt(s, 10);
    return isNaN(n) || n <= 0 ? null : n;
  }

  function openCartDrawer() {
    return Promise.resolve()
      .then(function () {
        var bundle = window.MonthleesMembershipBundle;
        if (bundle && typeof bundle.openCartAfterAdd === 'function') {
          return bundle.openCartAfterAdd({});
        }
        if (window.MonthleesCartDrawer) {
          if (typeof window.MonthleesCartDrawer.refresh === 'function') {
            return window.MonthleesCartDrawer.refresh({ force: true }).then(function () {
              if (typeof window.MonthleesCartDrawer.show === 'function') {
                window.MonthleesCartDrawer.show();
              } else if (typeof window.MonthleesCartDrawer.open === 'function') {
                window.MonthleesCartDrawer.open({ skipFetch: true });
              }
            });
          }
          if (typeof window.MonthleesCartDrawer.show === 'function') {
            window.MonthleesCartDrawer.show();
          }
        }
        return null;
      })
      .catch(function () {
        // Never fail the cart bridge because the drawer couldn't open — still try show().
        try {
          if (window.MonthleesCartDrawer && typeof window.MonthleesCartDrawer.show === 'function') {
            window.MonthleesCartDrawer.show();
          }
        } catch (e) {
          /* ignore */
        }
        return null;
      });
  }

  function syncEmbedCartChrome(count) {
    if (!root) return;
    var n = parseInt(count, 10);
    if (isNaN(n) || n < 0) n = 0;
    var chip = root.querySelector('[data-mg-embed-cart]');
    var countEls = root.querySelectorAll('[data-mg-embed-cart-count]');
    countEls.forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
    });
    if (chip) chip.hidden = n <= 0;
  }

  function hideCartConfirm() {
    if (!root) return;
    var banner = root.querySelector('[data-mg-embed-cart-confirm]');
    if (banner) banner.hidden = true;
  }

  function showCartConfirm() {
    if (!root) return;
    var banner = root.querySelector('[data-mg-embed-cart-confirm]');
    if (!banner) return;
    banner.hidden = false;
  }

  function postCartResult(result) {
    var payload = result || { ok: false, error: 'Cart bridge failed' };
    postToApp(MSG.CART_RESULT, payload);
    if (payload.ok) {
      if (payload.cart_count != null) {
        syncEmbedCartChrome(payload.cart_count);
        document.querySelectorAll('[data-monthlees-cart-count]').forEach(function (badge) {
          var n = parseInt(payload.cart_count, 10) || 0;
          if (n > 0) {
            badge.textContent = String(n);
            if (!badge.hasAttribute('data-mg-embed-cart-count')) badge.hidden = false;
          }
        });
      }
      showCartConfirm();
      document.dispatchEvent(new CustomEvent('monthlees:cart-updated'));
    }
  }

  function fetchCart() {
    var stackCart = window.MonthleesPrecisionStackCart;
    if (stackCart && typeof stackCart.fetchCart === 'function') {
      return stackCart.fetchCart();
    }
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    });
  }

  /** Shared replace-not-append helper (assets/monthlees-precision-stack-cart.js). */
  function removeExistingPrecisionStacks() {
    var stackCart = window.MonthleesPrecisionStackCart;
    if (!stackCart || typeof stackCart.removeExistingPrecisionStacks !== 'function') {
      return Promise.resolve(null);
    }
    return stackCart.removeExistingPrecisionStacks({
      stackSellingPlanId: config && config.stackSellingPlanId,
    });
  }

  function addVariantToCart(payload) {
    var variantId = normalizeVariantId(payload.shopify_variant_id);
    if ((!variantId || variantId === 0) && config.testVariantId) {
      variantId = normalizeVariantId(config.testVariantId);
    }
    var qty = parseInt(payload.quantity, 10);
    if (!qty || qty < 1) qty = 1;
    var propsIn = payload.properties || {};
    var packId = payload.pack_id || propsIn._pack_id || '';
    // Accept legacy bare result_id from older app payloads; always write private.
    var resultId =
      payload.result_id || propsIn._result_id || propsIn.result_id || '';
    var properties = {};
    if (packId) properties._pack_id = String(packId);
    if (resultId) properties._result_id = String(resultId);

    if (!variantId) {
      return Promise.resolve({
        ok: false,
        error: 'Missing shopify_variant_id',
      });
    }

    var item = {
      id: variantId,
      quantity: qty,
      properties: properties,
    };
    // Per-item plan only when the payload provides one (membership Join, APPROVE).
    // Absent/0 → one-time add — do not force the Precision stack selling plan.
    var planId = normalizePlanId(payload.selling_plan_id);
    if (planId) item.selling_plan = planId;

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [item] }),
    })
      .then(function (res) {
        return res
          .json()
          .then(function (data) {
            if (!res.ok) {
              var msg =
                (data && (data.description || data.message)) ||
                'Cart add failed (' + res.status + ')';
              return { ok: false, error: msg };
            }
            return fetchCart()
              .then(function (cart) {
                var count = cart && cart.item_count != null ? cart.item_count : null;
                return openCartDrawer().then(
                  function () {
                    return { ok: true, cart_count: count };
                  },
                  function () {
                    // Drawer open failed — still confirm the add to the app + chrome.
                    return { ok: true, cart_count: count };
                  }
                );
              })
              .catch(function () {
                return openCartDrawer().then(
                  function () {
                    return { ok: true, cart_count: null };
                  },
                  function () {
                    return { ok: true, cart_count: null };
                  }
                );
              });
          })
          .catch(function () {
            return { ok: false, error: 'Cart add failed (' + res.status + ')' };
          });
      })
      .catch(function () {
        return { ok: false, error: 'Network error adding to cart' };
      });
  }

  /** Approve = replace: drop prior Precision stack line(s), then add the new one. */
  function approvePrecisionStack(payload) {
    var body = {
      shopify_variant_id: payload.shopify_variant_id,
      quantity: 1,
      pack_id: payload.pack_id,
      result_id: payload.result_id,
      selling_plan_id: config.stackSellingPlanId,
      properties: {
        _pack_id: payload.pack_id,
        _result_id: payload.result_id,
      },
    };
    return removeExistingPrecisionStacks()
      .catch(function () {
        // Still attempt add if cleanup fails (e.g. empty/race); surface via CART_RESULT if add fails.
        return null;
      })
      .then(function () {
        return addVariantToCart(body);
      });
  }

  function handleCartMessage(type, payload) {
    var body = payload || {};
    var action;
    if (type === MSG.APPROVE) {
      action = approvePrecisionStack(body);
    } else {
      // Upsells / other products — append as-is (no stack de-dup).
      action = addVariantToCart(body);
    }
    return action
      .then(function (result) {
        postCartResult(result);
        captureEvent('monthlees_guide_cart_bridge', {
          type: type,
          ok: !!(result && result.ok),
          replaced_stack: type === MSG.APPROVE,
          surface: (openOpts && openOpts.surface) || 'unknown',
        });
        if (type === MSG.APPROVE && result && result.ok) {
          captureEvent('guide_stack_approved', {
            entry_surface: (openOpts && openOpts.entry_surface) || 'unknown',
          });
          setChromeSubtitle('formula');
        }
      })
      .catch(function (err) {
        postCartResult({
          ok: false,
          error: (err && err.message) || 'Cart bridge failed',
        });
        captureEvent('monthlees_guide_cart_bridge', {
          type: type,
          ok: false,
          replaced_stack: type === MSG.APPROVE,
          surface: (openOpts && openOpts.surface) || 'unknown',
        });
      });
  }

  function handleNavigate(url) {
    if (!url || typeof url !== 'string') return;
    var trimmed = url.trim();
    if (!trimmed) return;
    // Same-origin paths or absolute URLs to this shop only.
    try {
      if (trimmed.indexOf('//') === 0) return;
      if (/^https?:/i.test(trimmed)) {
        var u = new URL(trimmed);
        if (u.origin !== window.location.origin) return;
        window.location.assign(u.pathname + u.search + u.hash);
        return;
      }
      if (trimmed.charAt(0) !== '/') return;
      window.location.assign(trimmed);
    } catch (e) {
      /* ignore */
    }
  }

  function onMessage(event) {
    if (!root || !isOpen()) return;
    if (!event || !event.data || typeof event.data !== 'object') return;
    if (appOrigin && event.origin !== appOrigin) return;

    var type = event.data.type;
    var payload = event.data.payload || {};

    if (type === MSG.READY) {
      ready = true;
      sendInit();
      return;
    }
    if (type === MSG.AUTH_EXPIRED) {
      // Keep seededQueue / openOpts.draft — AUTH_EXPIRED must not drop the first message.
      fetchSession().then(function (result) {
        var scope = (openOpts && openOpts.scope) || (config && config.defaultScope) || 'precision';
        if (result.status === 401) {
          if (allowsGuestScope(scope)) {
            session = { token: null, expires_at: null, scope: scope };
            postToApp(MSG.AUTH_TOKEN, { token: null, expires_at: null });
            return;
          }
          showGate(
            (seededQueue && seededQueue.message) ||
              (openOpts && openOpts.draft) ||
              readDraft()
          );
          return;
        }
        if (result.status === 200 && result.data && result.data.token) {
          session = {
            token: result.data.token,
            expires_at: result.data.expires_at || null,
            scope: result.data.scope || scope,
          };
          postToApp(MSG.AUTH_TOKEN, {
            token: session.token,
            expires_at: session.expires_at,
          });
          // Re-send INIT with queued seed if the prior handshake was incomplete.
          if (!initSent) sendInit();
        }
      });
      return;
    }
    if (type === MSG.APPROVE || type === MSG.ADD_TO_CART) {
      handleCartMessage(type, payload);
      return;
    }
    if (type === MSG.RESIZE) {
      // Content auto-height only for the inline homepage module (3b).
      // Full-surface + slide-over use a fixed viewport/drawer height with internal scroll.
      if (!root.classList.contains('mg-embed--inline')) return;
      var h = parseInt(payload.height, 10);
      if (!isNaN(h) && h > 0 && frame) {
        var max = Math.max(320, window.innerHeight - 120);
        frame.style.height = Math.min(h, max) + 'px';
      }
      return;
    }
    if (type === MSG.NAVIGATE) {
      handleNavigate(payload.url);
      return;
    }
    if (type === MSG.THREAD_STATE) {
      var count = parseInt(payload.message_count != null ? payload.message_count : payload.messages, 10);
      if (payload.has_formula || payload.formula) {
        setChromeSubtitle('formula');
      } else if (!isNaN(count) && count > 0) {
        setChromeSubtitle('active');
      } else if (payload.has_user_message || payload.active) {
        setChromeSubtitle('active');
      } else {
        syncChromeSubtitleFromOpen(openOpts, '');
      }
    }
  }

  function bindUi() {
    if (!root || bound) return;
    bound = true;

    root.querySelectorAll('[data-mg-embed-close]').forEach(function (el) {
      el.addEventListener('click', closeShell);
    });

    root.querySelectorAll('[data-mg-embed-cart-open]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        openCartDrawer();
      });
    });

    var confirmDismiss = root.querySelector('[data-mg-embed-cart-confirm-dismiss]');
    if (confirmDismiss) {
      confirmDismiss.addEventListener('click', function () {
        hideCartConfirm();
      });
    }

    var scrim = root.querySelector('[data-mg-embed-scrim]');
    if (scrim) {
      scrim.addEventListener('click', function () {
        if (root.classList.contains('mg-embed--drawer')) closeShell();
      });
    }

    var dismiss = root.querySelector('[data-mg-embed-gate-dismiss]');
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        closeShell();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        event.preventDefault();
        closeShell();
      }
    });

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-mg-open-agent]');
      if (!trigger) return;
      event.preventDefault();
      var draft = trigger.getAttribute('data-mg-draft') || readDraft();
      var scope = trigger.getAttribute('data-mg-scope') || config.defaultScope || 'precision';
      var surface = trigger.getAttribute('data-mg-surface') || 'drawer';
      var resume = trigger.getAttribute('data-mg-resume') || '';
      var entrySurface =
        trigger.getAttribute('data-magnus-entry-surface') ||
        trigger.getAttribute('data-mg-entry-surface') ||
        'unknown';
      openShell({
        draft: draft,
        message: draft,
        scope: scope,
        surface: surface,
        resume: resume,
        entry_surface: entrySurface,
        context: inferContext({ context: trigger.getAttribute('data-magnus-context') }),
      });
    });

    window.addEventListener('message', onMessage);
  }

  function maybeAutoOpen() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('mg_open') === '1') {
        openShell({
          draft: readDraft(),
          resume: readResume() || params.get('resume') || '',
          surface: params.get('mg_surface') || 'drawer',
          scope: params.get('scope') || config.defaultScope,
          context: 'guide_page',
          entry_surface: 'guide_page',
        });
        return;
      }
      if (sessionStorage.getItem(PENDING_OPEN_KEY) === '1') {
        sessionStorage.removeItem(PENDING_OPEN_KEY);
        openShell({
          draft: readDraft(),
          resume: readResume(),
          surface: 'drawer',
          scope: config.defaultScope,
          context: 'guide_page',
          entry_surface: 'guide_page',
        });
        return;
      }
    } catch (e) {
      /* ignore */
    }

    var path = (window.location.pathname || '').toLowerCase();
    if ((path.indexOf('/pages/magnus') === 0 || path.indexOf('/pages/guide') === 0) && readDraft()) {
      openShell({
        draft: readDraft(),
        resume: readResume(),
        surface: 'drawer',
        scope: 'precision',
        context: 'guide_page',
        entry_surface: 'guide_page',
      });
    }
  }

  function runDevAssertions() {
    var debug =
      (window.Shopify && window.Shopify.designMode) ||
      /(?:\?|&)magnus_debug=1(?:&|$)/.test(window.location.search || '');
    if (!debug) return;

    var animated = [];
    document.querySelectorAll('.magnus-mark.is-live, .magnus-mark.is-working').forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) animated.push(el);
    });
    if (animated.length > 1) {
      console.warn('[magnus_debug] >1 animated .magnus-mark in viewport', animated);
    }

    document.querySelectorAll('.magnus-mark').forEach(function (el) {
      var id = el.getAttribute('data-magnus-mark') || '';
      var size = parseFloat(getComputedStyle(el).getPropertyValue('--magnus-size')) || 0;
      if (size > 0 && size < 16 && id !== '5m' && id !== '5n') {
        console.warn('[magnus_debug] magnus-mark below 16px that is not 5m/5n', el, size);
      }
    });

    document.querySelectorAll('[data-magnus-composer]').forEach(function (el) {
      var btn = el.querySelector('[data-magnus-composer-submit]');
      if (!btn || !String(btn.textContent || '').trim()) {
        console.warn('[magnus_debug] composer missing submit_label', el);
      }
    });

    var state = document.documentElement.getAttribute('data-magnus-state') || 'idle';
    if (state === 'idle') {
      document.querySelectorAll('.magnus-mark.is-live').forEach(function (el) {
        console.warn('[magnus_debug] is-live while data-magnus-state is idle', el);
      });
    }
  }

  function initGuideComposer(rootEl) {
    var form = rootEl.querySelector('[data-magnus-composer-form]');
    var input = rootEl.querySelector('[data-magnus-composer-input]');
    if (!form || !input) return;

    var surface = rootEl.getAttribute('data-magnus-entry-surface') || 'unknown';
    var context = rootEl.getAttribute('data-magnus-context') || 'home';
    var scope = rootEl.getAttribute('data-magnus-scope') || 'commerce';

    input.addEventListener('focus', function () {
      captureEvent('guide_entry_focus', { entry_surface: surface, context: context });
    });

    function openFromText(text, extra) {
      var draft = String(text || '').trim();
      if (!draft) {
        input.focus();
        return;
      }
      var product = null;
      if (context === 'pdp') {
        product = readSelectedProduct({
          product_id: rootEl.getAttribute('data-magnus-product-id'),
          product_handle: rootEl.getAttribute('data-magnus-product-handle'),
          product_title: rootEl.getAttribute('data-magnus-product-title'),
          variant_id: rootEl.getAttribute('data-magnus-variant-id'),
        });
      }
      openShell(
        Object.assign(
          {
            message: draft,
            draft: draft,
            scope: scope,
            surface: surface === 'bar_1b' ? 'drawer' : 'drawer',
            context: context,
            entry_surface: surface,
            product: product,
          },
          extra || {}
        )
      );
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      openFromText(input.value);
    });

    rootEl.querySelectorAll('[data-magnus-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var prompt = chip.getAttribute('data-magnus-chip-prompt') || chip.textContent;
        captureEvent('guide_chip_click', {
          entry_surface: surface,
          chip_label: String(chip.textContent || '').trim(),
        });
        input.value = prompt;
        openFromText(prompt);
      });
    });
  }

  function initPdpNudge(nudge) {
    var ask = nudge.querySelector('[data-magnus-pdp-ask]');
    if (!ask) return;
    captureEvent('guide_entry_view', { entry_surface: 'pdp_2a', context: 'pdp' });
    ask.addEventListener('click', function () {
      var title = nudge.getAttribute('data-magnus-product-title') || 'this product';
      var product = readSelectedProduct({
        product_id: nudge.getAttribute('data-magnus-product-id'),
        product_handle: nudge.getAttribute('data-magnus-product-handle'),
        product_title: title,
        variant_id: nudge.getAttribute('data-magnus-variant-id'),
      });
      var message = 'Does ' + title + ' fit my stack?';
      openShell({
        message: message,
        draft: message,
        scope: 'commerce',
        surface: 'drawer',
        context: 'pdp',
        entry_surface: 'pdp_2a',
        product: product,
      });
    });
  }

  function initPublicApi() {
    var api = window.MonthleesMagnus || {};
    api.open = function (opts) {
      if (!root) init();
      if (!root) return Promise.reject(new Error('Magnus embed not mounted'));
      return openShell(opts || {});
    };
    api.close = closeShell;
    api.state = function () {
      return guideState;
    };
    api.copy = COPY;
    api.whenCapabilities = fetchCapabilitiesOnce;
    api.on = function (eventName, fn) {
      if (!listeners[eventName]) listeners[eventName] = [];
      listeners[eventName].push(fn);
      return function off() {
        listeners[eventName] = (listeners[eventName] || []).filter(function (f) {
          return f !== fn;
        });
      };
    };
    api.track = function (name, props) {
      captureEvent(name, props || {});
    };
    api.readDraft = readDraft;
    api.writeDraft = writeDraft;
    api.markEntered = markEntered;
    api.openAgent = api.open;
    Object.defineProperty(api, 'capabilities', {
      get: function () {
        return capabilitiesPayload;
      },
      configurable: true,
    });
    window.MonthleesMagnus = api;
  }

  function init() {
    root = document.querySelector('[data-mg-embed]');
    if (!root) return;
    config = parseConfig();
    frame = root.querySelector('[data-mg-embed-frame]');
    panel = root.querySelector('[data-mg-embed-panel]');
    gate = root.querySelector('[data-mg-embed-gate]');
    statusEl = root.querySelector('[data-mg-embed-status]');
    echoEl = root.querySelector('[data-mg-embed-echo]');
    setGuideState('idle');
    initPublicApi();
    bindUi();
    fetchCapabilitiesOnce();
    document.querySelectorAll('[data-magnus-composer]').forEach(initGuideComposer);
    document.querySelectorAll('[data-magnus-pdp-nudge]').forEach(initPdpNudge);
    maybeAutoOpen();
    window.setTimeout(runDevAssertions, 800);
  }

  window.MonthleesMagnusAgent = {
    open: function (opts) {
      if (!root) init();
      if (!root) return Promise.reject(new Error('Magnus embed not mounted'));
      return openShell(opts || {});
    },
    close: closeShell,
    isOpen: isOpen,
    readDraft: readDraft,
    writeDraft: writeDraft,
    markPendingOpen: function () {
      try {
        sessionStorage.setItem(PENDING_OPEN_KEY, '1');
      } catch (e) {
        /* ignore */
      }
    },
  };

  initPublicApi();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* COMPAT_ALIAS_MAGNUS */
  window.MonthleesGuide = window.MonthleesMagnus;
  window.MonthleesGuideAgent = window.MonthleesMagnusAgent;
})();
